// src/services/encargadosExtras.js - Servicio de gestión de encargados extra

import AuthService from "../auth/auth.js";

/**
 * Servicio para manejar operaciones con encargados extra de locales
 * Conecta con el endpoint /usuarios/api/encargados/ del backend
 */
class EncargadosExtrasService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpoint = "/usuarios/api/encargados/";
  }

  /**
   * Obtiene todos los encargados extra de un local
   * @param {number} localId - ID del local
   * @returns {Promise<Array>} Array de encargados extra
   */
  async obtenerEncargadosPorLocal(localId) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}?id_local=${localId}`);

      // Manejo robusto de respuesta
      let encargados = [];
      if (Array.isArray(response)) {
        encargados = response;
      } else if (response && Array.isArray(response.results)) {
        encargados = response.results;
      }

      return encargados;
    } catch (error) {
      console.error(`❌ Error obteniendo encargados extra de local ${localId}:`, error);
      throw this.manejarError(error);
    }
  }

  /**
   * Crea un nuevo encargado extra
   * @param {Object} datosEncargado - Datos del encargado
   * @returns {Promise<Object>} Encargado creado
   */
  async crearEncargado(datosEncargado) {
    try {
      console.log("👤 Creando encargado extra:", datosEncargado);

      // Validar datos
      this.validarDatosEncargado(datosEncargado);

      // Preparar datos
      const payload = {
        nombre: datosEncargado.nombre.trim(),
        telefono: datosEncargado.telefono ? this.limpiarTelefono(datosEncargado.telefono) : "",
        email: datosEncargado.email?.trim() || "",
        cargo: datosEncargado.cargo?.trim() || "",
        id_local: datosEncargado.id_local,
      };

      // Obtener tokens
      const tokens = this.obtenerTokens();

      // Realizar petición
      const response = await fetch(`${this.baseURL}${this.endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const responseData = await this.procesarRespuesta(response);
      console.log("  Encargado extra creado exitosamente:", responseData);

      return responseData;
    } catch (error) {
      console.error("❌ Error creando encargado extra:", error);
      throw this.manejarError(error);
    }
  }

  /**
   * Actualiza un encargado extra existente
   * @param {number} encargadoId - ID del encargado
   * @param {Object} datosEncargado - Datos actualizados
   * @returns {Promise<Object>} Encargado actualizado
   */
  async actualizarEncargado(encargadoId, datosEncargado) {
    try {
      console.log(`🔄 Actualizando encargado extra ${encargadoId}:`, datosEncargado);

      // Validar datos
      this.validarDatosEncargado(datosEncargado);

      // Preparar datos
      const payload = {
        nombre: datosEncargado.nombre.trim(),
        telefono: datosEncargado.telefono ? this.limpiarTelefono(datosEncargado.telefono) : "",
        email: datosEncargado.email?.trim() || "",
        cargo: datosEncargado.cargo?.trim() || "",
      };

      // No incluir id_local en actualización si no cambió
      if (datosEncargado.id_local) {
        payload.id_local = datosEncargado.id_local;
      }

      // Obtener tokens
      const tokens = this.obtenerTokens();

      // Realizar petición
      const response = await fetch(`${this.baseURL}${this.endpoint}${encargadoId}/`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const responseData = await this.procesarRespuesta(response);
      console.log("  Encargado extra actualizado exitosamente:", responseData);

      return responseData;
    } catch (error) {
      console.error(`❌ Error actualizando encargado extra ${encargadoId}:`, error);
      throw this.manejarError(error);
    }
  }

  /**
   * Elimina un encargado extra
   * @param {number} encargadoId - ID del encargado a eliminar
   * @returns {Promise<boolean>} Éxito de la operación
   */
  async eliminarEncargado(encargadoId) {
    try {
      console.log(`🗑️ Eliminando encargado extra ${encargadoId}...`);

      const tokens = this.obtenerTokens();

      const response = await fetch(`${this.baseURL}${this.endpoint}${encargadoId}/`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      console.log(`  Encargado extra ${encargadoId} eliminado exitosamente`);
      return true;
    } catch (error) {
      console.error(`❌ Error eliminando encargado extra ${encargadoId}:`, error);
      throw this.manejarError(error);
    }
  }

  /**
   * Crea múltiples encargados extra en lote
   * @param {Array} encargados - Array de objetos con datos de encargados
   * @param {number} localId - ID del local
   * @returns {Promise<Array>} Array de encargados creados
   */
  async crearEncargadosEnLote(encargados, localId) {
    try {
      console.log(`👥 Creando ${encargados.length} encargados extra para local ${localId}...`);

      const promesas = encargados.map((encargado) => this.crearEncargado({ ...encargado, id_local: localId }));

      const resultados = await Promise.all(promesas);
      console.log(`  ${resultados.length} encargados extra creados exitosamente`);

      return resultados;
    } catch (error) {
      console.error("❌ Error creando encargados en lote:", error);
      throw this.manejarError(error);
    }
  }

  // ==========================================
  // MÉTODOS HELPER PRIVADOS
  // ==========================================

  /**
   * Valida los datos básicos del encargado
   * @param {Object} datosEncargado - Datos a validar
   */
  validarDatosEncargado(datosEncargado) {
    if (!datosEncargado) {
      throw new Error("Los datos del encargado son requeridos");
    }

    if (!datosEncargado.nombre || !datosEncargado.nombre.trim()) {
      throw new Error("El nombre del encargado es requerido");
    }

    // Validar longitudes
    if (datosEncargado.nombre.length > 100) {
      throw new Error("El nombre no puede tener más de 100 caracteres");
    }

    if (datosEncargado.cargo && datosEncargado.cargo.length > 100) {
      throw new Error("El cargo no puede tener más de 100 caracteres");
    }

    // Validar email si se proporciona
    if (datosEncargado.email && datosEncargado.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(datosEncargado.email)) {
        throw new Error("El formato del email no es válido");
      }
    }

    // Validar teléfono si se proporciona
    if (datosEncargado.telefono && datosEncargado.telefono.trim()) {
      const telefonoLimpio = this.limpiarTelefono(datosEncargado.telefono);
      if (telefonoLimpio.length < 8) {
        throw new Error("El teléfono debe tener al menos 8 dígitos");
      }
    }

    // Validar que tenga id_local al crear
    if (!datosEncargado.id_local && !datosEncargado.id) {
      throw new Error("El ID del local es requerido");
    }
  }

  /**
   * Limpia el formato del teléfono dejando solo dígitos y +
   * @param {string} telefono - Teléfono a limpiar
   * @returns {string} Teléfono limpio
   */
  limpiarTelefono(telefono) {
    if (!telefono) return "";
    // Mantener solo dígitos y el símbolo +
    return telefono.replace(/[^\d+]/g, "");
  }

  /**
   * Obtiene tokens de autenticación desde cookies
   * @returns {Object} Objeto con tokens csrf y jwt
   */
  obtenerTokens() {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    return {
      csrf: getCookie("csrftoken"),
      jwt: getCookie("iiotefintel_cookie"),
    };
  }

  /**
   * Procesa la respuesta HTTP y maneja errores
   * @param {Response} response - Respuesta fetch
   * @returns {Promise<Object>} Datos de respuesta parseados
   */
  async procesarRespuesta(response) {
    const responseText = await response.text();

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Error del servidor (${response.status}): ${responseText}`);
      }

      // Construir mensaje de error detallado
      let mensajeError = "Error al procesar encargado:\n";

      if (errorData.nombre) {
        const nombreErrors = Array.isArray(errorData.nombre) ? errorData.nombre : [errorData.nombre];
        mensajeError += `• Nombre: ${nombreErrors.join(", ")}\n`;
      }

      if (errorData.telefono) {
        const telefonoErrors = Array.isArray(errorData.telefono) ? errorData.telefono : [errorData.telefono];
        mensajeError += `• Teléfono: ${telefonoErrors.join(", ")}\n`;
      }

      if (errorData.email) {
        const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
        mensajeError += `• Email: ${emailErrors.join(", ")}\n`;
      }

      if (errorData.non_field_errors) {
        const generalErrors = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
        mensajeError += `• ${generalErrors.join(", ")}\n`;
      }

      throw new Error(mensajeError.trim() || `Error del servidor (${response.status})`);
    }

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error("Respuesta del servidor no válida");
    }
  }

  /**
   * Maneja errores y los convierte en mensajes amigables
   * @param {Error} error - Error original
   * @returns {Error} Error procesado
   */
  manejarError(error) {
    let mensaje = "Error desconocido";
    let codigo = "UNKNOWN_ERROR";

    if (error.message?.includes("Sesión expirada")) {
      mensaje = "Sesión expirada. Inicie sesión nuevamente.";
      codigo = "SESSION_EXPIRED";
    } else if (error.message?.includes("No hay sesión activa")) {
      mensaje = "No hay sesión activa. Inicie sesión.";
      codigo = "NO_SESSION";
    } else if (error.message?.includes("Error 401")) {
      mensaje = "No autorizado. Inicie sesión nuevamente.";
      codigo = "UNAUTHORIZED";
    } else if (error.message?.includes("Error 403")) {
      mensaje = "Sin permisos para gestionar encargados";
      codigo = "FORBIDDEN";
    } else if (error.message?.includes("Error 404")) {
      mensaje = "Encargado no encontrado";
      codigo = "NOT_FOUND";
    } else if (error.message?.includes("Error 500")) {
      mensaje = "Error interno del servidor";
      codigo = "SERVER_ERROR";
    } else if (error.message?.includes("Failed to fetch")) {
      mensaje = "Error de conexión. Verifique su internet.";
      codigo = "NETWORK_ERROR";
    } else if (error.message) {
      mensaje = error.message;
    }

    const errorProcesado = new Error(mensaje);
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }
}

// Exportar instancia única del servicio
export default new EncargadosExtrasService();
